import { z } from 'zod';
import { IdSchema, MoneySchema, TimestampSchema } from './common.js';

/**
 * Invoicing (docs/28 B2; doc 15 §8).
 *
 * An invoice is drafted, then issued. Issuing takes its number and freezes it,
 * with the issuer's and the customer's details as they were that day; a
 * correction is a credit note. Payments recorded by hand are allocated to
 * invoices, and invoices never move the airtime balance.
 *
 * The tax invoice fields follow doc 15 §8 and still need the accountant's
 * confirmation before the first real invoice (doc 22 §7).
 */

export const DateOnlySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'A date: YYYY-MM-DD');

export const INVOICE_STATUSES = ['draft', 'issued', 'part_paid', 'paid', 'credited'] as const;
export const InvoiceStatusSchema = z.enum(INVOICE_STATUSES);
export type InvoiceStatus = z.infer<typeof InvoiceStatusSchema>;

export const INVOICE_LINE_KINDS = ['seat', 'number', 'plan', 'add_on', 'usage', 'adjustment', 'other'] as const;
export const VAT_TREATMENTS = ['standard', 'zero_rated', 'exempt'] as const;
export const VatTreatmentSchema = z.enum(VAT_TREATMENTS);
export type VatTreatment = z.infer<typeof VatTreatmentSchema>;

const Quantity = z.string().trim().regex(/^-?\d{1,9}(\.\d{1,4})?$/, 'A quantity, such as 1 or 2.5');
const UnitPrice = z.string().trim()
  .regex(/^-?\d{1,12}(\.\d{1,6})?$/, 'An amount excluding VAT, such as 199.00');
const Amount = z.string().trim().regex(/^\d{1,12}(\.\d{1,2})?$/, 'An amount in rand, such as 1500.00');

export const InvoiceLineInputSchema = z.object({
  kind: z.enum(INVOICE_LINE_KINDS).default('other'),
  description: z.string().trim().min(1).max(300),
  quantity: Quantity,
  unit_price: UnitPrice,
  vat_treatment: VatTreatmentSchema.default('standard'),
}).strict();
export type InvoiceLineInput = z.input<typeof InvoiceLineInputSchema>;

/** A draft, created or replaced whole. */
export const InvoiceDraftSchema = z.object({
  tenant_id: IdSchema,
  customer_reference: z.string().trim().max(80).nullable().default(null),
  notes: z.string().trim().max(1000).nullable().default(null),
  period_start: DateOnlySchema.nullable().default(null),
  period_end: DateOnlySchema.nullable().default(null),
  lines: z.array(InvoiceLineInputSchema).min(1).max(200),
}).strict();
export type InvoiceDraft = z.input<typeof InvoiceDraftSchema>;

export const InvoiceIssuerSchema = z.object({
  name: z.string(),
  registered_name: z.string(),
  registration_number: z.string().nullable(),
  vat_number: z.string().nullable(),
  address: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  bank: z.object({
    name: z.string(), account_name: z.string(), account_number: z.string(), branch_code: z.string(),
  }).nullable(),
});
export type InvoiceIssuer = z.infer<typeof InvoiceIssuerSchema>;

export const InvoiceCustomerSchema = z.object({
  name: z.string(),
  address: z.string().nullable(),
  vat_number: z.string().nullable(),
});
export type InvoiceCustomer = z.infer<typeof InvoiceCustomerSchema>;

export const VatBreakdownSchema = z.array(z.object({
  treatment: VatTreatmentSchema, rate: z.string(), net: MoneySchema, vat: MoneySchema,
}));

export const InvoiceLineSchema = z.object({
  position: z.number().int(),
  kind: z.enum(INVOICE_LINE_KINDS),
  description: z.string(),
  quantity: z.string(),
  unit_price: z.string(),
  vat_treatment: VatTreatmentSchema,
  vat_rate: z.string(),
  amount: MoneySchema,
});
export type InvoiceLine = z.infer<typeof InvoiceLineSchema>;

export const InvoiceSummarySchema = z.object({
  id: IdSchema,
  number: z.string().nullable(),
  status: InvoiceStatusSchema,
  tenant_id: IdSchema.nullable(),
  account_name: z.string(),
  currency: z.string().length(3),
  issue_date: DateOnlySchema.nullable(),
  due_date: DateOnlySchema.nullable(),
  total: MoneySchema,
  amount_due: MoneySchema,
  /** Past its due date with something still to pay. */
  overdue: z.boolean(),
  created_at: TimestampSchema,
});
export type InvoiceSummary = z.infer<typeof InvoiceSummarySchema>;

export const InvoiceSchema = InvoiceSummarySchema.extend({
  customer_reference: z.string().nullable(),
  notes: z.string().nullable(),
  period_start: DateOnlySchema.nullable(),
  period_end: DateOnlySchema.nullable(),
  subtotal: MoneySchema,
  vat_total: MoneySchema,
  vat_breakdown: VatBreakdownSchema,
  amount_paid: MoneySchema,
  amount_credited: MoneySchema,
  /** As printed on the day it was issued; on a draft, as it would print today. */
  issuer: InvoiceIssuerSchema.nullable(),
  customer: InvoiceCustomerSchema.nullable(),
  lines: z.array(InvoiceLineSchema),
  credit_notes: z.array(z.object({
    id: IdSchema, number: z.string(), issue_date: DateOnlySchema, reason: z.string(), total: MoneySchema,
  })),
  payments: z.array(z.object({
    receipt_number: z.string().nullable(), received_on: DateOnlySchema.nullable(),
    method: z.string(), reference: z.string().nullable(), amount: MoneySchema,
  })),
});
export type Invoice = z.infer<typeof InvoiceSchema>;

/**
 * A credit note against an issued invoice. Without lines, the whole invoice is
 * credited line for line, which is only possible while nothing has been credited.
 */
export const CreditNoteCreateSchema = z.object({
  reason: z.string().trim().min(3).max(500),
  lines: z.array(z.object({
    description: z.string().trim().min(1).max(300),
    quantity: z.string().trim().regex(/^\d{1,9}(\.\d{1,4})?$/, 'A quantity, such as 1'),
    unit_price: z.string().trim().regex(/^\d{1,12}(\.\d{1,6})?$/, 'An amount excluding VAT'),
    vat_treatment: VatTreatmentSchema,
  }).strict()).min(1).max(200).optional(),
}).strict();
export type CreditNoteCreate = z.input<typeof CreditNoteCreateSchema>;

export const MANUAL_PAYMENT_METHODS = ['eft', 'cash', 'other'] as const;

/** A payment that arrived outside PayFast, allocated to one or more of an account's invoices. */
export const ManualPaymentSchema = z.object({
  tenant_id: IdSchema,
  method: z.enum(MANUAL_PAYMENT_METHODS),
  reference: z.string().trim().max(80).nullable().default(null),
  received_on: DateOnlySchema,
  allocations: z.array(z.object({ invoice_id: IdSchema, amount: Amount }).strict()).min(1).max(50),
}).strict();
export type ManualPayment = z.input<typeof ManualPaymentSchema>;

/** Who an account's invoices are made out to. */
export const InvoiceDetailsSchema = z.object({
  name: z.string().nullable(),
  address: z.string().nullable(),
  vat_number: z.string().nullable(),
  /** Printed when no name is set. */
  account_name: z.string(),
});
export type InvoiceDetails = z.infer<typeof InvoiceDetailsSchema>;

export const InvoiceDetailsUpsertSchema = z.object({
  name: z.string().trim().min(1).max(160).nullable(),
  address: z.string().trim().min(1).max(400).nullable(),
  vat_number: z.string().trim().regex(/^4\d{9}$/, 'Ten digits, starting with 4').nullable(),
}).strict();
export type InvoiceDetailsUpsert = z.infer<typeof InvoiceDetailsUpsertSchema>;
